import React from 'react';
import { Control, UseFormSetValue, UseFormGetValues, useWatch, FieldValues, Path } from 'react-hook-form';

interface CopyFromUserActionProps<T extends FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  sourceNameField: Path<T>;
  sourcePrimaryEmailField: Path<T>;
  sourceFallbackEmailField: Path<T>;
  sourceMobileField?: Path<T>;
  targetNameField: Path<T>;
  targetEmailField: Path<T>;
  targetMobileField?: Path<T>;
}

export const CopyFromUserAction = <T extends FieldValues>({
  control,
  setValue,
  getValues,
  sourceNameField,
  sourcePrimaryEmailField,
  sourceFallbackEmailField,
  sourceMobileField,
  targetNameField,
  targetEmailField,
  targetMobileField,
}: CopyFromUserActionProps<T>) => {
  const [sourceName, sourcePrimary, sourceFallback] = useWatch({
    control,
    name: [sourceNameField, sourcePrimaryEmailField, sourceFallbackEmailField],
  });

  const hasName = sourceName && typeof sourceName === 'string' && sourceName.trim() !== '';
  const hasFallback = sourceFallback && typeof sourceFallback === 'string' && sourceFallback.trim() !== '';
  const hasPrimary = sourcePrimary && typeof sourcePrimary === 'string' && sourcePrimary.trim() !== '';

  if (!hasName && !hasFallback && !hasPrimary) {
    return null;
  }

  const handleCopy = () => {
    const currentName = getValues(sourceNameField) as string | undefined | null;
    const currentPrimary = getValues(sourcePrimaryEmailField) as string | undefined | null;
    const currentFallback = getValues(sourceFallbackEmailField) as string | undefined | null;

    if (currentName && currentName.trim() !== '') {
      setValue(targetNameField, currentName as any, { shouldValidate: true, shouldDirty: true });
    }

    let targetEmail = '';
    if (currentFallback && currentFallback.trim() !== '') {
      targetEmail = currentFallback;
    } else if (currentPrimary && currentPrimary.trim() !== '') {
      targetEmail = currentPrimary;
    }

    if (targetEmail) {
      setValue(targetEmailField, targetEmail as any, { shouldValidate: true, shouldDirty: true });
    }

    if (sourceMobileField && targetMobileField) {
      const currentMobile = getValues(sourceMobileField) as string | undefined | null;
      if (currentMobile && currentMobile.trim() !== '') {
        setValue(targetMobileField, currentMobile as any, { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 font-medium transition-colors"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
      </svg>
      Copy from User
    </button>
  );
};
